import { useState, useCallback, useMemo } from "react";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import type {
  IBulkImportPreviewRow,
  IBulkImportRow,
} from "@/interface/bulkImport.interface";
import { buildImportFile, normalizePreviewResponse } from "./bulkImport.utils";

type ImportStep = "upload" | "preview";

interface UseBulkImportReturn {
  step: ImportStep;
  headers: string[];
  rows: IBulkImportPreviewRow[];
  validRows: IBulkImportPreviewRow[];
  invalidRows: IBulkImportPreviewRow[];
  isAllValid: boolean;
  isDownloading: boolean;
  isUploading: boolean;
  isImporting: boolean;
  uploadedFile: File | null;
  downloadTemplate: () => Promise<void>;
  uploadAndPreview: (file: File) => Promise<void>;
  updateRow: (rowIndex: number, updatedData: IBulkImportRow) => void;
  submitImport: () => Promise<boolean>;
  reset: () => void;
}

const useBulkImport = (): UseBulkImportReturn => {
  const [step, setStep] = useState<ImportStep>("upload");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<IBulkImportPreviewRow[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const validRows = useMemo(() => rows.filter((row) => row.isValid), [rows]);
  const invalidRows = useMemo(() => rows.filter((row) => !row.isValid), [rows]);
  const isAllValid = useMemo(
    () => rows.length > 0 && invalidRows.length === 0,
    [rows, invalidRows],
  );

  const downloadTemplate = useCallback(async () => {
    setIsDownloading(true);
    try {
      const blob = await EmployeeOnBoardService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "employee_import_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToastNotification("success", "Template downloaded successfully");
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const uploadAndPreview = useCallback(async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await EmployeeOnBoardService.previewImport(formData);
      const normalized = normalizePreviewResponse(response);

      if (
        normalized.rows.length > 0 ||
        (response && response.status !== false)
      ) {
        setHeaders(normalized.headers);
        setRows(normalized.rows);
        setUploadedFile(file);
        setStep("preview");

        if (response?.message && !response?.status) {
          showToastNotification("warning", response.message);
        } else {
          showToastNotification(
            "success",
            response?.message || "File parsed successfully",
          );
        }
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to parse file or empty preview data",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const updateRow = useCallback(
    (rowIndex: number, updatedData: IBulkImportRow) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.rowIndex !== rowIndex) return row;

          return {
            ...row,
            data: updatedData,
            isValid: true,
            errors: [],
          };
        }),
      );
    },
    [],
  );

  const submitImport = useCallback(async (): Promise<boolean> => {
    if (!isAllValid) {
      showToastNotification(
        "danger",
        "Please fix all invalid rows before importing",
      );
      return false;
    }

    setIsImporting(true);
    try {
      const fileToUpload = buildImportFile(rows, uploadedFile?.name);
      const formData = new FormData();
      formData.append("file", fileToUpload);

      const response = await EmployeeOnBoardService.bulkImport(formData);
      if (response?.status) {
        showToastNotification(
          "success",
          `Successfully imported ${response?.data?.imported || rows.length} employees`,
        );
        return true;
      }

      showToastNotification("danger", response?.message || "Import failed");
      return false;
    } catch (error) {
      ThrowError(error);
      return false;
    } finally {
      setIsImporting(false);
    }
  }, [isAllValid, rows, uploadedFile]);

  const reset = useCallback(() => {
    setStep("upload");
    setHeaders([]);
    setRows([]);
    setUploadedFile(null);
  }, []);

  return {
    step,
    headers,
    rows,
    validRows,
    invalidRows,
    isAllValid,
    isDownloading,
    isUploading,
    isImporting,
    uploadedFile,
    downloadTemplate,
    uploadAndPreview,
    updateRow,
    submitImport,
    reset,
  };
};

export default useBulkImport;
